import {
  isFieldVisible,
  nextPageIndex,
  validatePage,
  type FormAnswers,
  type FormDefinition,
  type FormField,
  type FormValue,
} from '@serviceportal/shared';
import { ArrowLeft, ArrowRight, Paperclip, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, Checkbox, Input, Label, Select, Textarea } from '@/components/ui/primitives';
import { cn } from '@/lib/utils';

export interface UploadResult {
  token: string;
  name: string;
}

interface Props {
  definition: FormDefinition;
  onSubmit: (data: FormAnswers) => Promise<void>;
  /** Fehlt sie, sind Datei-Felder deaktiviert (z. B. in der Vorschau). */
  uploadFile?: (file: File) => Promise<UploadResult>;
  submitting?: boolean;
  /** Serverseitige Feldfehler (Pfad = Feldschlüssel). */
  serverErrors?: Record<string, string>;
  error?: string | null;
  className?: string;
}

/** Stellt eine Formulardefinition dar: Seiten, Verzweigungen, Sichtbarkeit und Prüfung. */
export function FormRenderer({ definition, onSubmit, uploadFile, submitting, serverErrors, error, className }: Props) {
  const [data, setData] = useState<FormAnswers>({});
  const [history, setHistory] = useState<number[]>([0]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fileNames, setFileNames] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const top = useRef<HTMLDivElement>(null);

  const pageIndex = history[history.length - 1]!;
  const page = definition.pages[pageIndex]!;
  const next = nextPageIndex(definition, pageIndex, data);
  const isLast = next === -1;
  const allErrors = { ...serverErrors, ...errors };

  const set = (key: string, value: FormValue) => {
    setData((d) => ({ ...d, [key]: value }));
    setErrors((e) => {
      const { [key]: _, ...rest } = e;
      return rest;
    });
  };

  const goNext = async () => {
    const pageErrors = validatePage(definition, pageIndex, data);
    setErrors(pageErrors);
    if (Object.keys(pageErrors).length) return;
    if (isLast) {
      // Nur Antworten der tatsächlich besuchten Seiten senden
      const visited = new Set(history.flatMap((i) => definition.pages[i]!.fields.map((f) => f.key)));
      await onSubmit(Object.fromEntries(Object.entries(data).filter(([k]) => visited.has(k))));
    } else {
      setHistory((h) => [...h, next]);
      top.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const upload = async (field: FormField, files: FileList | null) => {
    if (!files || !uploadFile) return;
    const current = (data[field.key] as string[] | undefined) ?? [];
    const max = field.validation?.maxFiles ?? 5;
    setUploadError(null);
    for (const file of Array.from(files).slice(0, Math.max(0, max - current.length))) {
      setUploading(field.key);
      try {
        const r = await uploadFile(file);
        setFileNames((n) => ({ ...n, [r.token]: r.name }));
        setData((d) => ({ ...d, [field.key]: [...((d[field.key] as string[] | undefined) ?? []), r.token] }));
      } catch (e) {
        setUploadError(`${file.name}: ${e instanceof Error ? e.message : 'Upload fehlgeschlagen'}`);
      } finally {
        setUploading(null);
      }
    }
  };

  return (
    <div ref={top} className={cn('flex flex-col gap-5', className)}>
      {definition.pages.length > 1 && (
        <div className="flex items-center gap-1.5" aria-label={`Seite ${history.length}`}>
          {history.map((_, i) => (
            <span key={i} className="h-1.5 flex-1 rounded-full bg-primary" />
          ))}
          {!isLast && <span className="h-1.5 flex-1 rounded-full bg-muted" />}
        </div>
      )}
      <div>
        <h2 className="text-lg font-semibold">{page.title}</h2>
        {page.description && <p className="mt-1 text-sm text-muted-foreground">{page.description}</p>}
      </div>
      {error && <Alert variant="destructive">{error}</Alert>}
      <div className="grid gap-4 sm:grid-cols-2">
        {page.fields
          .filter((f) => isFieldVisible(f, data))
          .map((f) => (
            <FieldInput
              key={f.id}
              field={f}
              value={data[f.key]}
              error={allErrors[f.key]}
              onChange={(v) => set(f.key, v)}
              onFiles={(files) => upload(f, files)}
              uploading={uploading === f.key}
              uploadEnabled={Boolean(uploadFile)}
              fileNames={fileNames}
            />
          ))}
      </div>
      {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}
      <div className="flex items-center justify-between gap-2 border-t pt-4">
        {history.length > 1 ? (
          <Button type="button" variant="ghost" onClick={() => setHistory((h) => h.slice(0, -1))}>
            <ArrowLeft /> Zurück
          </Button>
        ) : (
          <span />
        )}
        <Button type="button" onClick={goNext} loading={submitting && isLast} disabled={Boolean(uploading)}>
          {isLast ? definition.submitLabel : 'Weiter'} {!isLast && <ArrowRight />}
        </Button>
      </div>
    </div>
  );
}

function FieldInput({
  field: f,
  value,
  error,
  onChange,
  onFiles,
  uploading,
  uploadEnabled,
  fileNames,
}: {
  field: FormField;
  value: FormValue;
  error?: string;
  onChange: (v: FormValue) => void;
  onFiles: (files: FileList | null) => void;
  uploading: boolean;
  uploadEnabled: boolean;
  fileNames: Record<string, string>;
}) {
  const id = `ff-${f.id}`;
  const fileInput = useRef<HTMLInputElement>(null);
  const span = f.width === 'half' ? '' : 'sm:col-span-2';
  const label = (
    <Label htmlFor={id}>
      {f.label}
      {f.required && <span className="text-destructive"> *</span>}
    </Label>
  );
  const help = error ? (
    <p className="text-xs text-destructive" role="alert">
      {error}
    </p>
  ) : f.help ? (
    <p className="text-xs text-muted-foreground">{f.help}</p>
  ) : null;

  if (f.type === 'heading') return <h3 className="pt-2 text-base font-semibold sm:col-span-2">{f.label}</h3>;
  if (f.type === 'paragraph') return <p className="whitespace-pre-wrap text-sm text-muted-foreground sm:col-span-2">{f.label}</p>;

  if (f.type === 'checkbox') {
    return (
      <div className={cn('flex flex-col gap-1', span)}>
        <label className="flex items-start gap-2.5 text-sm">
          <Checkbox id={id} className="mt-0.5" checked={value === true} onCheckedChange={(c) => onChange(c === true)} aria-invalid={Boolean(error)} />
          <span>
            {f.label}
            {f.required && <span className="text-destructive"> *</span>}
          </span>
        </label>
        {help}
      </div>
    );
  }

  let control: React.ReactNode;
  const str = typeof value === 'string' ? value : value === undefined || value === null ? '' : String(value);
  const common = { id, 'aria-invalid': Boolean(error), placeholder: f.placeholder };
  switch (f.type) {
    case 'textarea':
      control = <Textarea {...common} rows={5} value={str} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'select':
      control = (
        <Select {...common} value={str} onChange={(e) => onChange(e.target.value || undefined)}>
          <option value="">Bitte wählen …</option>
          {f.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      );
      break;
    case 'radio':
      control = (
        <div role="radiogroup" aria-labelledby={id} className="grid gap-2">
          {f.options?.map((o) => (
            <label key={o.value} className={cn('flex cursor-pointer items-center gap-2.5 rounded-lg border p-3 text-sm transition-colors', value === o.value ? 'border-primary bg-primary/5' : 'hover:bg-muted/40')}>
              <input type="radio" name={f.key} className="accent-[var(--primary)]" checked={value === o.value} onChange={() => onChange(o.value)} />
              {o.label}
            </label>
          ))}
        </div>
      );
      break;
    case 'checkboxes': {
      const arr = Array.isArray(value) ? value : [];
      control = (
        <div className="grid gap-2 sm:grid-cols-2">
          {f.options?.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm">
              <Checkbox checked={arr.includes(o.value)} onCheckedChange={(c) => onChange(c ? [...arr, o.value] : arr.filter((x) => x !== o.value))} />
              {o.label}
            </label>
          ))}
        </div>
      );
      break;
    }
    case 'file': {
      const tokens = Array.isArray(value) ? value : [];
      control = (
        <div className="flex flex-col gap-2">
          <input ref={fileInput} type="file" multiple className="hidden" accept="image/*,application/pdf,.docx,.xlsx,.odt,.txt,.zip" onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" disabled={!uploadEnabled} loading={uploading} onClick={() => fileInput.current?.click()}>
              <Paperclip /> Datei auswählen
            </Button>
            {!uploadEnabled && <span className="text-xs text-muted-foreground">(in der Vorschau deaktiviert)</span>}
          </div>
          {tokens.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {tokens.map((t) => (
                <li key={t} className="flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs">
                  {fileNames[t] ?? 'Datei'}
                  <button type="button" aria-label="Datei entfernen" onClick={() => onChange(tokens.filter((x) => x !== t))}>
                    <X className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      );
      break;
    }
    default:
      control = (
        <Input
          {...common}
          type={f.type === 'email' ? 'email' : f.type === 'phone' ? 'tel' : f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
          inputMode={f.type === 'number' ? 'decimal' : undefined}
          autoComplete={f.mapsTo === 'email' ? 'email' : f.mapsTo === 'firstName' ? 'given-name' : f.mapsTo === 'lastName' ? 'family-name' : f.mapsTo === 'phone' ? 'tel' : f.mapsTo === 'street' ? 'street-address' : f.mapsTo === 'postalCode' ? 'postal-code' : f.mapsTo === 'city' ? 'address-level2' : f.mapsTo === 'company' ? 'organization' : undefined}
          value={str}
          onChange={(e) => onChange(f.type === 'number' ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value)}
        />
      );
  }

  return (
    <div className={cn('flex flex-col gap-1.5', span)}>
      {label}
      {control}
      {help}
    </div>
  );
}
